import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckIcon from '@mui/icons-material/Check';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Box, Button, CardMedia, CircularProgress, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import FallbackImg from '../../../assets/image-placeholder.svg';
import { CreditChip, VendorLine } from '../../../components/_mui';
import {
  CHECKING_WITH_VENDOR_TEXT,
  CURRENTLY_UNAVAILABLE_LABEL,
  DOES_NOT_FIT_REMAINING_TEXT,
  getReplacePicksAriaLabel,
  LOCK_NOTE_TEXT,
  MARKETPLACE_CARD_FOOTER_ROW_MIN_WIDTH,
  REPLACE_PICKS_LABEL,
  SWAP_A_PICK_LABEL,
  UNAVAILABLE_CARD_OPACITY,
} from '../../../constants/marketplace';
import { useImageWithFallback } from '../../../hooks/useImageWithFallback';
import { IMarketplaceCatalogItem } from '../../../types/marketplace';
import { isShippedByPartner } from '../../../util/marketplace';

interface IProps {
  item: IMarketplaceCatalogItem;
  inCart: boolean;
  canAdd: boolean;
  hasPicks: boolean;
  isChecking: boolean;
  isOtherSupplier: boolean;
  onOpen: (productId: string) => void;
  onRemove: (productId: string) => void;
  onAdd: (productId: string) => void;
  onReplace: (productId: string) => void;
}

const mediaSx: SxProps<Theme> = {
  width: '100%',
  aspectRatio: '1 / 1',
  objectFit: 'cover',
};

const lockNoteSx: SxProps<Theme> = { display: 'block', mt: 1, textWrap: 'pretty' };

const shippingIcon = <LocalShippingOutlinedIcon />;

const bodySx: SxProps<Theme> = {
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
};

const titleClampSx: SxProps<Theme> = {
  mt: 0.5,
  mb: 1,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

// Below this footer width the price and the button no longer fit on one line, so the footer stacks
// them, mobile style, with the button stretched to the card width.
const stackedFooterQuery = `@container (max-width: ${MARKETPLACE_CARD_FOOTER_ROW_MIN_WIDTH - 1}px)`;

const footerRowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  justifyContent: 'space-between',
  gap: 1.5,
  [stackedFooterQuery]: { flexDirection: 'column', alignItems: 'stretch' },
};

const actionButtonSx: SxProps<Theme> = {
  height: { xs: 42, sm: 36 },
  minWidth: 88,
  width: { xs: '100%', sm: 'auto' },
  fontSize: { xs: 15, sm: 13 },
  flexShrink: 0,
  whiteSpace: 'nowrap',
  [stackedFooterQuery]: { width: '100%' },
};

// Swap and replace are secondary to Add, so they read grey rather than in the brand colour.
const mutedActionButtonSx: SxProps<Theme> = { ...actionButtonSx, color: 'text.secondary', borderColor: 'divider' };

const unavailableRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0,
  color: 'text.secondary',
  '& svg': { fontSize: 18, flexShrink: 0 },
};

export const MarketplaceItemCard: React.FC<IProps> = ({
  item,
  inCart,
  canAdd,
  hasPicks,
  isChecking,
  isOtherSupplier,
  onOpen,
  onRemove,
  onAdd,
  onReplace,
}) => {
  const isUnavailable = item.available === false;
  const isLocked = isOtherSupplier && !inCart && !isUnavailable;
  const doesNotFit = !inCart && !canAdd;
  const isOverBudget = doesNotFit && !hasPicks;
  const canSwap = doesNotFit && hasPicks;

  const { src: imageSrc, onError: handleImageError } = useImageWithFallback(item.image_url, FallbackImg);

  const handleOpen = useCallback(() => onOpen(item.product_id), [onOpen, item.product_id]);

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove(item.product_id);
    },
    [onRemove, item.product_id],
  );

  const handleAddClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAdd(item.product_id);
    },
    [onAdd, item.product_id],
  );

  const handleReplaceClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onReplace(item.product_id);
    },
    [onReplace, item.product_id],
  );

  const lockNoteId = `${item.product_id}-lock-note`;

  const isShippedByOther = useMemo(
    () => isShippedByPartner(item.vendor, item.supplierName),
    [item.vendor, item.supplierName],
  );

  const cardSx: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: 1,
      overflow: 'hidden',
      cursor: isUnavailable ? 'default' : 'pointer',
      opacity: isUnavailable ? UNAVAILABLE_CARD_OPACITY : 1,
      backgroundColor: 'background.paper',
      border: 2,
      borderColor: inCart ? 'primary.main' : 'transparent',
      outline: inCart ? 'none' : '1px solid',
      outlineColor: 'divider',
      outlineOffset: -1,
      transition: ({ transitions }) => transitions.create(['border-color'], { duration: transitions.duration.shorter }),
    }),
    [inCart, isUnavailable],
  );

  const footerSx: SxProps<Theme> = useMemo(
    () => ({ mt: 'auto', pt: isLocked ? 1.5 : 2, containerType: 'inline-size' }),
    [isLocked],
  );

  const addButtonSx = useMemo(() => (canSwap ? mutedActionButtonSx : actionButtonSx), [canSwap]);

  const actionLabel = useMemo(() => {
    if (isOverBudget) {
      return 'Over budget';
    }

    return canSwap ? SWAP_A_PICK_LABEL : 'Add';
  }, [isOverBudget, canSwap]);

  const addButton = useMemo(
    () => (
      <Button
        variant="outlined"
        color={canSwap ? 'inherit' : 'primary'}
        onClick={handleAddClick}
        disabled={isOverBudget || isChecking}
        aria-label={isChecking ? CHECKING_WITH_VENDOR_TEXT : undefined}
        sx={addButtonSx}
      >
        {isChecking ? <CircularProgress size={18} color="inherit" /> : actionLabel}
      </Button>
    ),
    [canSwap, handleAddClick, isOverBudget, isChecking, addButtonSx, actionLabel],
  );

  // v1 allows one unit per product, so the picked state is a single button that removes the pick.
  const actionButton = useMemo(() => {
    if (isLocked) {
      return (
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleReplaceClick}
          aria-label={getReplacePicksAriaLabel(item.name)}
          aria-haspopup="dialog"
          aria-describedby={lockNoteId}
          sx={mutedActionButtonSx}
        >
          {REPLACE_PICKS_LABEL}
        </Button>
      );
    }

    if (inCart) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleRemoveClick}
          startIcon={<CheckIcon sx={{ fontSize: 18 }} />}
          sx={actionButtonSx}
        >
          Added
        </Button>
      );
    }

    if (canSwap) {
      return (
        <Tooltip title={DOES_NOT_FIT_REMAINING_TEXT} placement="top" arrow>
          <span>{addButton}</span>
        </Tooltip>
      );
    }

    return addButton;
  }, [isLocked, handleReplaceClick, item.name, lockNoteId, inCart, canSwap, handleRemoveClick, addButton]);

  return (
    <Box
      sx={cardSx}
      onClick={isUnavailable ? undefined : handleOpen}
      data-testid={`marketplace-card-${item.product_id}`}
    >
      <CardMedia component="img" image={imageSrc} alt={item.name} onError={handleImageError} sx={mediaSx} />
      <Box sx={bodySx}>
        <VendorLine vendor={item.vendor} reserveSpace />
        <Typography variant="body2" fontWeight="medium" sx={titleClampSx}>
          {item.name}
        </Typography>
        {isShippedByOther ? (
          <VendorLine prefix="Ships from" vendor={item.supplierName} icon={shippingIcon} />
        ) : (
          <VendorLine vendor="Ships from the brand" icon={shippingIcon} />
        )}
        {isLocked && (
          <Typography id={lockNoteId} variant="caption" color="text.primary" sx={lockNoteSx}>
            {LOCK_NOTE_TEXT}
          </Typography>
        )}
        <Box sx={footerSx}>
          <Box sx={footerRowSx} data-testid="marketplace-card-footer">
            {isUnavailable ? (
              <Box sx={unavailableRowSx}>
                <BlockOutlinedIcon />
                <Typography variant="body2" noWrap>
                  {CURRENTLY_UNAVAILABLE_LABEL}
                </Typography>
              </Box>
            ) : (
              <>
                <CreditChip credits={item.creditPrice} isFromPrice />
                {actionButton}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
